import { useEffect, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models";
import { Row, Col, Tabs, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CommitteeDateRangePicker } from "./components";
import { DEFAULT_COUNTY_COMMITTEE } from "./constants";
import {
  getCountyCommitteeById,
  createCountyCommitteeById,
  saveCountyCommittee,
  getCountyCommitteeInstitutionListExport,
} from "../../api/Committee/CountyCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuthCounties } from "../../hooks/auth";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import { ValidationFailure } from "../../models";
import {
  CountyCommittee,
  CommitteeEmployee,
  CommitteeType,
} from "../../models/Committee";
import { SaveResult } from "../../models/Shared";
import { useGetInstitutionListForUserQuery } from "../../queries/codeBookQuery";
import { countyCommitteeDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils } from "../../utils";
import { useDeleteCommittee } from "./hooks/useDeleteCommittee";
import {
  generatingEditedEmployee,
  getCommitteeEmployeeDisplayName,
  isEmployeeActive,
  updateEmployeesWithPresident,
  replaceEmployeeInTableData,
} from "./utils";
import {
  setupCommitteeEmployees,
  updateCommitteeEmployee,
} from "./utils/committee.util";
import { CommitteeModal } from "./components/CommitteeModal";

const DEFAULT_TAB = "members";

export function CountyCommitteeEdit() {
  const { id, countyId } = useParams<{ id: string; countyId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const counties = useAuthCounties();

  const [item, setItem] = useState<CountyCommittee>(DEFAULT_COUNTY_COMMITTEE);
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);

  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-5",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "edit",
        header: t("common.edit"),
        accessor: (employee) => {
          return (
            <div className={"text-right"}>
              <Button
                size="sm"
                outline={true}
                onClick={() => handleDetailsClicked(employee)}
                disabled={!isEditing}
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "expertCommissionPosition",
        header: t("common.expertCommission.position"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeePosition?.name}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          const isActive = isEmployeeActive(emp);

          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.rowKeyId!}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => handlePresidentChange(emp.rowKeyId!)}
                value={emp.rowKeyId!}
                disabled={!emp.employee || !isEditing || !isActive}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tabKey, setTabKey] = useState(DEFAULT_TAB);
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const [exportFormat, setExportFormat] = useState<ExportTypeEnum>(
    ExportTypeEnum.Excel
  );

  const { handleDeleteCommittee, isDeleteEnabled } = useDeleteCommittee({
    isEnabled: !item.hasDecisions,
    committeeType: "county",
    committeeId: +id,
  });

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        const result = await getCountyCommitteeById(+id);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
      if (!isNaN(+countyId) && +countyId > 0) {
        const result = await createCountyCommitteeById(+countyId);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, countyId]);

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
  };
  const handleDateChange = (date: Date, field: "dateFrom" | "dateTo") => {
    const newItem = {
      ...item,
      [field]: FormatUtils.setDateToUTCMidnight(date),
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleOnSaveCommitteeEmployee = (
    newCommitteeEmployee: CommitteeEmployee
  ) => {
    if (!editing) return;

    const employeeToEdit = generatingEditedEmployee(
      editing,
      newCommitteeEmployee
    );

    setTableData((prev) =>
      replaceEmployeeInTableData(prev, employeeToEdit)
    );
    setItem((currItem) => updateCommitteeEmployee(currItem, employeeToEdit));
    setEditing(null);
  };

  const handlePresidentChange = (rowKeyId: CommitteeEmployee["rowKeyId"]) => {
    const updatedEmployees = updateEmployeesWithPresident(tableData, rowKeyId);

    setTableData(updatedEmployees);
    setItem({
      ...item,
      employees: updatedEmployees,
    });
    dispatch(setIsEdited());
  };
  const handleNoteChange = (e: any) => {
    const note = e.target.value;
    const newItem: CountyCommittee = {
      ...item,
      note: note,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleOnCloseModal = () => {
    setEditing(null);
  };

  const handleDownload = async () => {
    if (!item.countyId) return;

    await getCountyCommitteeInstitutionListExport(item.countyId, exportFormat);
  };

  const deleteButtonVisibility = isEditing
    ? isDeleteEnabled
      ? "show"
      : "disable"
    : "hide";

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditing ? "hide" : "show"}
        pageTitle={t("common.expertCommission.countyPageNameEdit")}
        onClickEdit={() => {
          setIsEditing(true);
          dispatch(setIsEdited());
        }}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.county")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {counties.find((c) => c.id === item.countyId)?.name}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <CommitteeDateRangePicker
            dateFrom={item.dateFrom ? new Date(item.dateFrom) : null}
            dateTo={item.dateTo ? new Date(item.dateTo) : null}
            onDateFromChange={(date) => handleDateChange(date, "dateFrom")}
            onDateToChange={(date) => handleDateChange(date, "dateTo")}
            errors={errors}
            disabled={!isEditing}
            plaintext={!isEditing}
            colMd={2}
          />
          <Col md={4} />
        </Row>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={handleNoteChange}
              errorText={
                errors?.find((e) => e.propertyName === "Note")?.message
              }
              maxLength={5000}
              disabled={!isEditing}
            />
          </Col>
        </Row>
      </FormCard>
      <Tabs
        id="controlled-tab-example"
        activeKey={tabKey ?? "basicInfo"}
        onSelect={(k) => setTabKey(k ?? DEFAULT_TAB)}
      >
        <Tab
          title={t("common.expertCommission.tabs.memberList")}
          eventKey={DEFAULT_TAB}
          className={
            errors?.some((e) => e.propertyName?.startsWith("Employees"))
              ? "text-danger"
              : undefined
          }
        >
          <FormCard>
            <Table {...tableProps} />
          </FormCard>
        </Tab>
        <Tab
          title={t("common.expertCommission.tabs.institutionList")}
          eventKey="institutions"
          disabled={+id === 0}
          className={
            errors?.some((e) => e.propertyName?.startsWith("InstitutionList"))
              ? "text-danger"
              : undefined
          }
        >
          <ul>
            {institutions &&
              institutions
                ?.filter(
                  (v) => v.countyId === item.countyId && !v.hasRegionalCommittee
                )
                .map((v) => (
                  <li>
                    <Typography component="span">{v.name}</Typography>
                  </li>
                ))}
          </ul>
        </Tab>
      </Tabs>

      <ToolbarEdit
        isDataEdited={isEditing}
        showCancel={"show"}
        showSave={isEditing ? "show" : "hide"}
        showDownload={"show"}
        saveText={t("common.saveAndApprove")}
        handleSave={async () => saveCountyCommittee(item)}
        redirectUrlSaveOK={countyCommitteeDetailUrl}
        setSaveErrors={setErrors}
        onClickDownload={handleDownload}
        onClickChangeFormat={() =>
          exportFormat === ExportTypeEnum.Excel
            ? setExportFormat(ExportTypeEnum.Csv)
            : setExportFormat(ExportTypeEnum.Excel)
        }
        onSaveSuccess={(data: SaveResult<CountyCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
        showDelete={deleteButtonVisibility}
        onClickDelete={handleDeleteCommittee}
      />
      {/* CommitteeEmployee Modal */}
      <CommitteeModal
        committeeType={CommitteeType.County}
        editingEmployee={editing ?? null}
        onSave={handleOnSaveCommitteeEmployee}
        onClose={handleOnCloseModal}
      />
    </ContentArea>
  );
}
